import React, { FC, Fragment, useState } from 'react';
import { formatDate } from 'utils/format';
import { useTranslation } from 'react-i18next';
import { Invoice, InvoiceDelivery, InvoicePaymentStatus, InvoiceStatus, InvoicingType, Type } from 'models/invoice';
import { Box, Button, Divider, Typography } from '@mui/material';
import { useMutation } from '@apollo/client/react';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { retryCreateCollectiveInvoiceTemplate, retryCreateOrderInvoiceTemplate } from 'apollo/templates/invoices';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import Loader from 'components/shared/loader/loader.component';
import InvoiceProcessingError from './invoice-processing-error.component';
import { downloadCertificationPdf } from './files';

type InvoiceSummaryPanelProps = {
    invoice: Invoice;
    openSendInvoiceDialog: () => void;
    openPaidDateModal: () => void;
    openSetUnpaidModal: () => void;
    openSetSendDateDialog: () => void;
    openCreateCreditInvoiceDialog: () => void;
    isCertificationSettingEnabled: boolean;
    onRetryInvoiceCreationComplete: () => void;
};
const InvoiceSummaryPanel: FC<InvoiceSummaryPanelProps> = ({
    invoice,
    openSendInvoiceDialog,
    openPaidDateModal,
    openSetUnpaidModal,
    openSetSendDateDialog,
    openCreateCreditInvoiceDialog,
    isCertificationSettingEnabled,
    onRetryInvoiceCreationComplete,
}: InvoiceSummaryPanelProps) => {
    const { t: translate } = useTranslation(['invoices']);
    const [showLoader, setLoader] = useState(false);
    const currentUser = currentUserVar();
    const { showBoundary } = useDenthubErrorBoundary();

    const [retryCreateOrderInvoice] = useMutation(retryCreateOrderInvoiceTemplate, {
        variables: {
            invoiceId: invoice._id,
        },
        onCompleted: () => {
            onRetryInvoiceCreationComplete();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [retryCreateCollectiveInvoice] = useMutation(retryCreateCollectiveInvoiceTemplate, {
        variables: {
            invoiceId: invoice._id,
        },
        onCompleted: () => {
            onRetryInvoiceCreationComplete();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const generateCertificationPdf = async () => {
        try {
            setLoader(true);
            await downloadCertificationPdf(invoice);
        } finally {
            setLoader(false);
        }
    };

    const shouldShowPaidBtn = (invoice: Invoice) => {
        if (invoice.isCredited) {
            return false;
        }
        if (
            invoice.deliveryMethod === InvoiceDelivery.ELECTRONIC_BILLING ||
            invoice.deliveryMethod === InvoiceDelivery.SCHEDULED_ELECTRONIC
        ) {
            return invoice.status === InvoiceStatus.SENT;
        }
        return true;
    };

    const handleRetryClick = (invoice: Invoice) => {
        if (
            invoice.invoiceType === InvoicingType.COLLECTIVE ||
            invoice.invoiceType === InvoicingType.COLLECTIVE_DENTIST
        ) {
            retryCreateCollectiveInvoice({ variables: { invoiceId: invoice._id } });
        } else if (invoice.invoiceType === InvoicingType.SINGLE) {
            retryCreateOrderInvoice({ variables: { invoiceId: invoice._id } });
        }
    };

    return (
        <Fragment>
            <Box mb={1}>
                <Box>
                    <Typography variant="subtitle2">
                        {translate(invoice?.deliveryMethod || 'ELECTRONIC_INVOICING')},{' '}
                        {translate(invoice?.invoiceType || 'SINGLE')}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="subtitle2">
                        {invoice?.sentDate ? (
                            <span>
                                {translate('SENT')}: {formatDate(invoice?.sentDate, 'date')}
                            </span>
                        ) : (
                            translate('NOT_SENT')
                        )}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="subtitle2">
                        {invoice?.paidOn ? (
                            <span>
                                {translate('PAID')}: {formatDate(invoice?.paidOn, 'date')}
                            </span>
                        ) : undefined}
                    </Typography>
                </Box>
            </Box>

            <Box mb={1}>
                {invoice.orders?.length > 0 &&
                isCertificationSettingEnabled &&
                (invoice.invoiceType === InvoicingType.COLLECTIVE ||
                    invoice.invoiceType === InvoicingType.COLLECTIVE_DENTIST) &&
                permissions.isDentist(currentUser.type) &&
                !showLoader ? (
                    <Button variant="contained" size="medium" fullWidth onClick={generateCertificationPdf}>
                        {translate('invoices:CREATE_CERTIFICATION_PDF')}
                    </Button>
                ) : undefined}
            </Box>
            {showLoader ? <Loader /> : undefined}
            {invoice?.pdfUrl ? (
                <Box mb={1}>
                    <Button
                        disabled={invoice.status === InvoiceStatus.ERROR}
                        variant="contained"
                        size="medium"
                        fullWidth
                        target="_blank"
                        rel="noreferrer"
                        href={invoice?.pdfUrl}
                    >
                        {translate('invoices:DOWNLOAD_INVOICE_PDF')}
                    </Button>
                </Box>
            ) : undefined}
            {permissions.isLabTechnician(currentUser.type) ? (
                <Fragment>
                    {invoice.deliveryMethod === InvoiceDelivery.EMAIL ? (
                        <Box mb={1}>
                            <Button
                                disabled={invoice.status === InvoiceStatus.ERROR}
                                variant="contained"
                                size="medium"
                                fullWidth
                                onClick={() => openSendInvoiceDialog()}
                            >
                                {translate('invoices:SEND_INVOICE_BY_EMAIL')}
                            </Button>
                        </Box>
                    ) : undefined}
                    {/* DD-3644: e-mail invoices are included because an invoice created with
                        "Skicka e-post automatiskt" unticked finishes as "Skapad" - its e-mail
                        never left Denthub. The lab can either send it from the button above or
                        record here that it was delivered some other way. */}
                    {(invoice.deliveryMethod === InvoiceDelivery.PRINT ||
                        invoice.deliveryMethod === InvoiceDelivery.EXTERNAL_INVOICE_SYSTEM ||
                        invoice.deliveryMethod === InvoiceDelivery.EMAIL) &&
                    invoice.status !== InvoiceStatus.SENT ? (
                        <Box mb={1}>
                            <Button
                                disabled={invoice.status === InvoiceStatus.ERROR}
                                variant="contained"
                                size="medium"
                                fullWidth
                                onClick={() => openSetSendDateDialog()}
                            >
                                {translate('invoices:REGISTER_SENT_DATE')}
                            </Button>
                        </Box>
                    ) : undefined}
                    {shouldShowPaidBtn(invoice) ? (
                        !invoice.paymentStatus || invoice.paymentStatus === InvoicePaymentStatus.UNPAID ? (
                            <Box mb={1}>
                                <Button
                                    disabled={invoice.status === InvoiceStatus.ERROR}
                                    variant="contained"
                                    size="medium"
                                    fullWidth
                                    onClick={() => openPaidDateModal()}
                                >
                                    {translate('invoices:SET_PAID')}
                                </Button>
                            </Box>
                        ) : (
                            <Box mb={1}>
                                <Button
                                    disabled={invoice.status === InvoiceStatus.ERROR}
                                    variant="contained"
                                    size="medium"
                                    fullWidth
                                    onClick={() => openSetUnpaidModal()}
                                >
                                    {translate('invoices:SET_UNPAID')}
                                </Button>
                            </Box>
                        )
                    ) : undefined}
                    {invoice?.invoiceSpecificationPdfUrl ? (
                        <Box mb={1}>
                            <Button
                                disabled={invoice.status === InvoiceStatus.ERROR}
                                variant="contained"
                                size="medium"
                                fullWidth
                                target="_blank"
                                rel="noreferrer"
                                href={invoice?.invoiceSpecificationPdfUrl}
                            >
                                {translate('invoices:DOWNLOAD_INVOICE_SPECIFICATION_PDF')}
                            </Button>
                        </Box>
                    ) : undefined}
                </Fragment>
            ) : undefined}

            {invoice?.deliveryNotesPdfUrl ? (
                <Box mb={1}>
                    <Button
                        disabled={invoice.status === InvoiceStatus.ERROR}
                        variant="contained"
                        size="medium"
                        fullWidth
                        target="_blank"
                        rel="noreferrer"
                        href={invoice?.deliveryNotesPdfUrl}
                    >
                        {translate('invoices:DOWNLOAD_DELIVERY_NOTES_PDF')}
                    </Button>
                </Box>
            ) : undefined}
            <Box mb={1}>
                <Divider />
            </Box>

            {permissions.isLabTechnician(currentUser.type) &&
            !invoice.isCredited &&
            invoice.type === Type.DEBIT &&
            (invoice.status === InvoiceStatus.CREATED ||
                invoice.status === InvoiceStatus.PROCESSING ||
                invoice.status === InvoiceStatus.SENT ||
                invoice.status === InvoiceStatus.SCHEDULED ||
                invoice.status === InvoiceStatus.EXTERNAL_INVOICE_ERROR) ? (
                <Box>
                    <Button color="error" fullWidth size="medium" onClick={() => openCreateCreditInvoiceDialog()}>
                        {translate('invoices:CREDIT_THIS_INVOICE')}
                    </Button>
                </Box>
            ) : undefined}

            {permissions.isLabTechnician(currentUser.type) && invoice.status === InvoiceStatus.ERROR ? (
                <Box>
                    {invoice.processingErrorMessage ? (
                        <InvoiceProcessingError message={invoice.processingErrorMessage} />
                    ) : undefined}
                    <Button variant="contained" fullWidth size="medium" onClick={() => handleRetryClick(invoice)}>
                        {translate('invoices:RETRY_INVOICE_CREATION')}
                    </Button>
                </Box>
            ) : undefined}

            {/* DD-3504: the invoice exists in Fortnox, only the delivery-note attachment failed.
                Deliberately no retry button — a retry would re-run the whole invoice pipeline. */}
            {permissions.isLabTechnician(currentUser.type) &&
            invoice.status === InvoiceStatus.EXTERNAL_INVOICE_ERROR ? (
                <InvoiceProcessingError
                    severity="warning"
                    titleKey="invoices:EXTERNAL_INVOICE_ERROR_REASON"
                    message={invoice.processingErrorMessage ?? ''}
                />
            ) : undefined}
        </Fragment>
    );
};

export default InvoiceSummaryPanel;
